import { ColumnDef } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Download, Grid2X2, Loader, TagIcon } from "lucide-react";
import { Link } from "react-router";

import { ClearFilters } from "@/components/clear-filters";
import { ResponsiveButton } from "@/components/responsive-button";
import { ServerDataTable } from "@/components/server-data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilterDropdownMenu } from "@/components/verifications/filter-dropdown-menu";
import { useExportTagVerificationsMutation } from "@/data/mutations";
import { useGetVerificationsQuery } from "@/data/queries";
import {
  TagVerification,
  TagVerificationType,
  TagVerificationTypeLabels,
} from "@/data/types";

const columns: ColumnDef<TagVerification>[] = [
  {
    header: "Tag ID",
    accessorKey: "uid",
    cell: ({ row }) => {
      return (
        <Link to={`/tags/${row.original.uid}`}>
          <Button variant="outline" size="sm">
            <TagIcon /> {row.original.uid}
          </Button>
        </Link>
      );
    },
  },
  {
    header: "Type",
    accessorKey: "verify_type",
    cell: ({ row }) => {
      switch (row.original.verify_type) {
        case TagVerificationType.ProgrammingQC:
          return (
            <Badge variant="purple">
              {TagVerificationTypeLabels[TagVerificationType.ProgrammingQC]}
            </Badge>
          );
        case TagVerificationType.UserView:
          return (
            <Badge variant="blue">
              {TagVerificationTypeLabels[TagVerificationType.UserView]}
            </Badge>
          );
        case TagVerificationType.Custom:
        default:
          return (
            <Badge variant="green">
              {row.original.custom_verification_type?.name ||
                TagVerificationTypeLabels[TagVerificationType.Custom]}
            </Badge>
          );
      }
    },
  },
  {
    id: "template_id",
    accessorKey: "template_id",
    header: "SKU ID",
    cell: ({ row }) => {
      return (
        <Link to={`/skus/${row.original.template_id}`}>
          <Button variant="outline" size="sm">
            <Grid2X2 /> {row.original.template_id}
          </Button>
        </Link>
      );
    },
  },
  {
    id: "templates.name",
    accessorKey: "name",
    header: "SKU Name",
  },
  {
    id: "nft_token_id",
    accessorKey: "nft_token_id",
    header: "Serial Number",
  },
  {
    id: "tag_verifications.created_at",
    accessorKey: "created_at",
    header: "Verified at",
    cell: ({ row }) => {
      return (
        <div>
          {parseISO(row.original.created_at).toLocaleDateString()}
          <br />
          {parseISO(row.original.created_at).toLocaleTimeString()}
        </div>
      );
    },
  },
  {
    header: "Tamper Status",
    accessorKey: "tamper",
    cell: ({ row }) => {
      if (!row.original.tamper) {
        return <Badge variant="secondary">--</Badge>;
      } else if (row.original.tamper === "closed") {
        return <Badge variant="secondary">Sealed</Badge>;
      } else if (row.original.tamper === "open") {
        return <Badge>Opened</Badge>;
      }
    },
  },
  {
    id: "counter",
    accessorKey: "counter",
    header: "Read Sequence ID",
    cell: ({ row }) => {
      return <div>{row.original.counter}</div>;
    },
  },
  {
    header: "City, Region, Country",
    accessorKey: "city",
    cell: ({ row }) => {
      return (
        <div>
          {[row.original.city, row.original.region, row.original.country]
            .filter(Boolean)
            .join(", ") || "Unknown"}
        </div>
      );
    },
  },
  {
    header: "Latitude, Longitude",
    accessorKey: "latitude",
    cell: ({ row }) => {
      return (
        <div>
          {[row.original.latitude, row.original.longitude]
            .filter(Boolean)
            .join(", ") || "Unknown"}
        </div>
      );
    },
  },
  {
    header: "CMAC",
    accessorKey: "cmac",
  },
];

const VERIFICATIONS_DEFAULT_SORT = {
  column: "tag_verifications.created_at",
  direction: "desc",
} as const;

export const Verifications = () => {
  const { data, isError, error, isFetching } = useGetVerificationsQuery({
    defaultSort: VERIFICATIONS_DEFAULT_SORT,
  });
  const exportMutation = useExportTagVerificationsMutation({
    defaultSort: VERIFICATIONS_DEFAULT_SORT,
  });

  const handleExport = async () => {
    exportMutation.mutateAsync();
  };

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div>
      <h2 className="scroll-m-20 border-b pb-2 text-3xl font-medium tracking-tight first:mt-0">
        Verifications
      </h2>
      <ServerDataTable
        isLoading={isFetching}
        columns={columns}
        data={data?.verifications}
        paginationInfo={data?.pagy}
        filterInputColumn="uid"
        filterPlaceholder="Filter by Tag ID"
        filterButtons={
          <div className="flex gap-2">
            <FilterDropdownMenu />
            <ClearFilters
              keys={["templateId", "verifyType", "customVerificationTypeId"]}
            />
          </div>
        }
        extraButtons={
          <ResponsiveButton
            variant="secondary"
            disabled={exportMutation.isPending}
            onClick={handleExport}
            leftIcon={
              exportMutation.isPending ? (
                <Loader className="animate-spin" />
              ) : (
                <Download />
              )
            }
          >
            Download CSV
          </ResponsiveButton>
        }
        defaultColumnVisibility={{
          latitude: false,
          counter: false,
          cmac: false,
        }}
        sortableColumns={[
          "template_id",
          "templates.name",
          "nft_token_id",
          "tag_verifications.created_at",
          "counter",
        ]}
        defaultSort={VERIFICATIONS_DEFAULT_SORT}
      />
    </div>
  );
};
